import { useSelector } from "react-redux";

import { RootState } from "../../../store/store";
import { renderIcon } from "../../../utils/ultis";
import "./logoSpinner.css";

interface IProps
{
  /** Outer ring diameter in px. Everything inside scales from it. */
  size?: number;
  /** antd's <Spin indicator> clones the element and injects `ant-spin-dot` here. */
  className?: string;
}

// The ring is drawn in a 100x100 viewBox so it stays perfectly concentric with
// the logo at any `size`. r=46 leaves room for the 4-unit stroke.
const R = 46;
const CIRCUMFERENCE = 2 * Math.PI * R;
const ARC = CIRCUMFERENCE * 0.28;

/**
 * The app's single loading indicator: the system logo with a ring spinning
 * around it. Used directly, as an overlay through `LoadingGlobalComp`, and as
 * the default antd Spin indicator (registered on ConfigProvider in App.tsx), so
 * every loading state in the app looks the same.
 */
const LogoSpinner = ({ size = 64, className = "" }: IProps) =>
{
  const commonSystem = useSelector(
    (state: RootState) => state.appCommonSlice.commonSystem,
  );
  const colorConfig = useSelector(
    (state: RootState) => state.appCommonSlice.colorConfig,
  );

  const ringColor = colorConfig?.loading?.color || "#4CC2FF";

  return (
    // Size is inline so it beats `.ant-spin-dot` (a 1em square) when this is
    // used as an antd indicator; `fontSize` makes the inner `em` sizes follow.
    <div
      className={`logo-spinner ${className}`}
      style={{ width: size, height: size, fontSize: size }}
    >
      <svg className="logo-spinner__ring" viewBox="0 0 100 100">
        <circle
          className="logo-spinner__track"
          cx="50"
          cy="50"
          r={R}
          fill="none"
          strokeWidth="4"
        />
        <circle
          className="logo-spinner__arc"
          cx="50"
          cy="50"
          r={R}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          stroke={ringColor}
          strokeDasharray={`${ARC} ${CIRCUMFERENCE - ARC}`}
        />
      </svg>

      <span className="logo-spinner__logo">
        {commonSystem?.logo ? (
          renderIcon(
            commonSystem?.logo?.type,
            commonSystem?.logo,
            "logo-spinner__img",
          )
        ) : (
          <img className="logo-spinner__img" src="/default-logo.png" alt="" />
        )}
      </span>
    </div>
  );
};

export default LogoSpinner;
